import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import usePost from '../../hooks/usePost';
import { formatDistanceToNow } from 'date-fns';

/**
 * PostCard component for displaying a post
 * @param {Object} props - Component props
 * @param {Object} props.post - Post data
 * @param {boolean} props.showActions - Whether to show post actions
 * @returns {JSX.Element} PostCard component
 */
const PostCard = ({ post, showActions = true }) => {
  const { user, isAuth } = useAuth();
  const { likePost, unlikePost } = usePost();
  const [isLiking, setIsLiking] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);

  const renderPostContent = () => {
    // Return null for invalid posts
    if (!post || !post._id) {
      console.warn('Invalid post data passed to PostCard:', post);
      return null;
    }

    const isLiked = user && post.likes && Array.isArray(post.likes) && post.likes.includes(user._id);

    const postDate = new Date(post.createdAt);
    const formattedDate = formatDistanceToNow(postDate, { addSuffix: true });

    const MAX_CONTENT_LENGTH = 280;
    const content = post.content || '';
    const isContentTruncated = content.length > MAX_CONTENT_LENGTH;
    const displayContent = showFullContent || !isContentTruncated
      ? content
      : `${content.substring(0, MAX_CONTENT_LENGTH)}...`;

    const handleImageClick = (imageUrl) => {
      setExpandedImage(imageUrl);
    };

    const handleCloseExpandedImage = () => {
      setExpandedImage(null);
    };
    
    return (
      <>
        {/* Expanded Image Modal */}
        {expandedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={handleCloseExpandedImage}
          >
            <div className="max-w-4xl max-h-screen p-2">
              <img 
                src={expandedImage} 
                alt="Expanded view" 
                className="max-w-full max-h-[90vh] object-contain"
              />
              <button 
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75"
                onClick={handleCloseExpandedImage}
              >
                <svg 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4 overflow-hidden">
          {/* Post header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-start">
              <Link to={`/profile/${post.user?.username || 'user'}`}>
                <Avatar
                  src={post.user?.profilePicture}
                  alt={post.user?.name || 'User'}
                  size="md"
                  isOnline={post.user?.isOnline}
                />
              </Link>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <Link
                      to={`/profile/${post.user?.username || 'user'}`}
                      className="font-medium text-gray-900 hover:underline"
                    >
                      {post.user?.name || 'User'}
                    </Link>
                    <span className="mx-1 text-gray-500">·</span>
                    <Link
                      to={`/profile/${post.user?.username || 'user'}`}
                      className="text-gray-500 hover:underline"
                    >
                      @{post.user?.username || 'user'}
                    </Link>
                  </div>
                  <div className="text-gray-400 text-sm">
                    <time dateTime={post.createdAt} title={postDate.toLocaleString()}>
                      {formattedDate}
                    </time>
                  </div>
                </div>
                
                {/* Community tag if post is in a community */}
                {post.community && (
                  <div className="mt-1">
                    <Link
                      to={`/communities/${post.community.slug || ''}`}
                      className="text-xs bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-1 text-gray-700"
                    >
                      {post.community.name || 'Community'}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Post content */}
          <div className="px-4 py-3">
            <div className="text-gray-800 whitespace-pre-wrap break-words">
              {displayContent}
              
              {isContentTruncated && (
                <button
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-1"
                  onClick={() => setShowFullContent(!showFullContent)}
                >
                  {showFullContent ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          </div>
          
          {/* Post media */}
          {post.media && post.media.length > 0 && (
            <div className="border-t border-gray-100">
              <div className={`grid ${post.media.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
                {post.media.map((item, index) => (
                  <div key={index} className="relative">
                    {item.type === 'image' && (
                      <div className="relative overflow-hidden" style={{ maxHeight: '350px' }}>
                        <img
                          src={item.url}
                          alt="Post attachment"
                          className="w-full h-auto object-contain cursor-pointer mx-auto"
                          style={{ maxHeight: '350px' }}
                          onClick={() => handleImageClick(item.url)}
                        />
                      </div>
                    )}
                    {item.type === 'video' && (
                      <div className="aspect-w-16 aspect-h-9" style={{ maxHeight: '350px' }}>
                        <video
                          src={item.url}
                          controls
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    {item.type === 'document' && (
                      <div className="w-full p-4 bg-gray-50 flex items-center">
                        <svg
                          className="h-8 w-8 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          View document
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Post stats and actions */}
          {showActions && (
            <div className="px-4 py-3 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  {/* Like button */}
                  <button
                    className={`flex items-center ${
                      isLiked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'
                    }`}
                    onClick={() => handleLikeToggle(post._id, isLiked)}
                    disabled={isLiking || !isAuth}
                  >
                    <svg
                      className={`h-5 w-5 ${isLiked ? 'fill-current' : 'stroke-current'}`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth={isLiked ? '0' : '2'}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    <span className="ml-1 text-sm">{post.likesCount || 0}</span>
                  </button>
                  
                  {/* Comment button */}
                  <Link
                    to={`/posts/${post._id}`}
                    className="flex items-center text-gray-500 hover:text-blue-600"
                  >
                    <svg
                      className="h-5 w-5 stroke-current"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <span className="ml-1 text-sm">{post.commentsCount || 0}</span>
                  </Link>
                  
                  {/* Share button */}
                  <button className="flex items-center text-gray-500 hover:text-green-600">
                    <svg
                      className="h-5 w-5 stroke-current"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      />
                    </svg>
                  </button>
                </div>
                
                {/* View full post */}
                <Link to={`/posts/${post._id}`}>
                  <Button variant="light" size="sm">
                    View Post
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // Handle like/unlike
  const handleLikeToggle = async (postId, isCurrentlyLiked) => {
    if (!isAuth) return;
    
    try {
      setIsLiking(true);
      if (isCurrentlyLiked) {
        await unlikePost(postId);
      } else {
        await likePost(postId);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };
  
  return renderPostContent();
};

PostCard.propTypes = {
  post: PropTypes.object.isRequired,
  showActions: PropTypes.bool,
};

export default PostCard;